// Type definitions
export interface ProfileAliasDto {
  idAliasID: string;
  chrBaseUserName: string;
  chrAliasName: string;
  chrDisplayName: string;
  intAliasType: number;  // 1=Public, 2=Private, 3=Semi-Anonymous, 4=Business/Credentialed
  aliasTypeName: string; // Human-readable: "Public", "Private", "Semi-Anonymous", "Business"
  chrCredentialType?: string; // For type 4: "VENDOR", "MARKETER", "DEVELOPER"
  bitIsActive: boolean;
  bitIsPrimary: boolean;
  bitTrueNameLocked?: boolean;
  bitIdVerified?: boolean;
  dtmCreateDate: string;
  dtmLastUsedDate?: string;
}

export interface CreateAliasRequestDto {
  chrAliasName: string;
  chrDisplayName: string;
  intAliasType: number; // 1=Public, 2=Private, 3=Semi-Anonymous
}

export interface UpdateAliasRequestDto {
  chrDisplayName?: string;
  intAliasType?: number; // 1=Public, 2=Private, 3=Semi-Anonymous
  bitIsActive?: boolean;
}

export interface TrueNameLockRequestDto {
  lockEnabled: boolean;
}

export interface ProfileDto {
  idProfileID: string;
  idAliasID: string;
  chrAliasName: string;
  chrDisplayName: string;
  txtBio?: string;
  chrProfileImageURL?: string;
  chrCoverImageURL?: string;
  chrLocation?: string;
  chrWebsiteURL?: string;
  intVisibilityLevel: number;
  chrJobTitle?: string;
  chrCompany?: string;
  txtSkills?: string;
  chrEducation?: string;
  txtProfessionalBio?: string;
  dtmCreateDate?: string;
  dtmLastModifiedDate?: string;
  socialLinks: SocialLinkDto[];
  interests: InterestDto[];
  // Business account info (only populated for business/credentialed aliases)
  intAliasType?: number;
  createdByUsername?: string;
  businessName?: string;
  credentialType?: string;
}

export interface ProfileAccessDto {
  profile: ProfileDto;
  canEdit: boolean;
  canView?: boolean;
  viewDeniedReason?: string;
  isTeamMemberProfile: boolean;
  isPremiumAliasProfile: boolean;
}

export interface ProfileSummaryDto {
  idProfileID: string;
  idAliasID: string;
  chrAliasName: string;
  chrDisplayName: string;
  intVisibilityLevel: number;
  intAliasType?: number;
  chrLocation?: string;
  chrJobTitle?: string;
  chrCompany?: string;
  avatarThumbUrl?: string;
  dtmLastModifiedDate?: string;
  canViewProfile: boolean;
}

export interface ProfileFetchResult<T> {
  data: T | null;
  notModified: boolean;
  etag?: string;
}

export interface UpdateProfileRequestDto {
  chrDisplayName?: string;
  txtBio?: string;
  chrProfileImageURL?: string;
  chrCoverImageURL?: string;
  chrLocation?: string;
  chrWebsiteURL?: string;
  intVisibilityLevel?: number;
  chrJobTitle?: string;
  chrCompany?: string;
  txtSkills?: string;
  chrEducation?: string;
  txtProfessionalBio?: string;
}

export interface SocialLinkDto {
  idSocialLinkID: string;
  idProfileID: string;
  chrPlatform: string;
  chrPlatformURL: string;
  chrPlatformUsername?: string;
  intDisplayOrder: number;
  bitIsActive: boolean;
}

export interface CreateSocialLinkDto {
  chrPlatform: string;
  chrPlatformURL: string;
  chrPlatformUsername?: string;
  intDisplayOrder?: number;
}

export interface InterestDto {
  idInterestID: string;
  idProfileID: string;
  chrInterestTag: string;
  chrInterestCategory?: string;
}

export interface CreateInterestDto {
  chrInterestTag: string;
  chrInterestCategory?: string;
}

import api from './api';

function resolveApiBaseUrl(): string {
  const envUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
  if (envUrl) {
    return envUrl;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'stmail.qixit.com' || host === 'mail.qixit.com') {
      return '';
    }
  }
  return '';
}

class SocialService {
  private readonly baseUrl: string;

  constructor() {
    const apiBaseUrl = resolveApiBaseUrl();
    this.baseUrl = apiBaseUrl ? `${apiBaseUrl}/api` : '/api';
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Alias Management
  async getMyAliases(): Promise<ProfileAliasDto[]> {
    const response = await fetch(`${this.baseUrl}/alias/me`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch aliases');
    }
    return response.json();
  }

  async getAliasByName(aliasName: string): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias/name/${aliasName}`);
    if (!response.ok) {
      throw new Error('Failed to fetch alias');
    }
    return response.json();
  }

  async createAlias(request: CreateAliasRequestDto): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create alias');
    }
    return response.json();
  }

  async updateAlias(aliasId: string, request: UpdateAliasRequestDto): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const text = await response.text();
    if (!response.ok) {
      let message = 'Failed to update alias';
      if (text) {
        try {
          const error = JSON.parse(text);
          message = error.message || message;
        } catch {
          message = text.slice(0, 100) || message;
        }
      }
      throw new Error(message);
    }
    if (!text) {
      throw new Error('Server returned no data');
    }
    try {
      return JSON.parse(text) as ProfileAliasDto;
    } catch {
      throw new Error('Invalid response from server');
    }
  }

  async setPrimaryAlias(aliasId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}/primary`, {
      method: 'PUT',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to set primary alias');
    }
  }

  async deleteAlias(aliasId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete alias');
    }
  }

  async setTrueNameLock(aliasId: string, lockEnabled: boolean): Promise<ProfileAliasDto> {
    const request: TrueNameLockRequestDto = { lockEnabled };
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}/true-name-lock`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const text = await response.text();
    if (!response.ok) {
      let message = 'Failed to update true name lock';
      if (text) {
        try {
          const error = JSON.parse(text);
          message = error.message || message;
        } catch {
          message = text.slice(0, 120) || message;
        }
      }
      throw new Error(message);
    }

    return text ? (JSON.parse(text) as ProfileAliasDto) : ({} as ProfileAliasDto);
  }

  // Profile Management
  async getProfile(
    aliasName: string,
    options?: { ifNoneMatch?: string },
  ): Promise<ProfileFetchResult<ProfileAccessDto>> {
    const headers: HeadersInit = { ...this.getAuthHeaders() };
    if (options?.ifNoneMatch) {
      headers['If-None-Match'] = options.ifNoneMatch;
    }

    const response = await fetch(`${this.baseUrl}/profile/alias/${encodeURIComponent(aliasName)}`, {
      headers,
    });
    const etag = response.headers.get('ETag') ?? undefined;

    if (response.status === 304) {
      return { data: null, notModified: true, etag: options?.ifNoneMatch ?? etag };
    }

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    const data = (await response.json()) as ProfileAccessDto;
    return { data, notModified: false, etag };
  }

  async getProfileSummary(
    aliasName: string,
    options?: { ifNoneMatch?: string },
  ): Promise<ProfileFetchResult<ProfileSummaryDto>> {
    const headers: HeadersInit = { ...this.getAuthHeaders() };
    if (options?.ifNoneMatch) {
      headers['If-None-Match'] = options.ifNoneMatch;
    }

    const response = await fetch(
      `${this.baseUrl}/profile/alias/${encodeURIComponent(aliasName)}/summary`,
      { headers },
    );
    const etag = response.headers.get('ETag') ?? undefined;

    if (response.status === 304) {
      return { data: null, notModified: true, etag: options?.ifNoneMatch ?? etag };
    }

    if (response.status === 404) {
      return { data: null, notModified: false, etag };
    }

    if (!response.ok) {
      throw new Error('Failed to fetch profile summary');
    }

    const data = (await response.json()) as ProfileSummaryDto;
    return { data, notModified: false, etag };
  }

  async uploadProfileMedia(aliasId: string, mediaKind: 'avatar' | 'cover', file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const body = await api.post<{ url: string }>(
        `/api/profile/${aliasId}/media/${mediaKind}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (!body?.url) {
        throw new Error('No URL returned from server');
      }
      return body.url;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const message =
        axiosErr.response?.data?.message ||
        axiosErr.message ||
        'Failed to upload image';
      throw new Error(message);
    }
  }

  async getProfileByAliasId(aliasId: string): Promise<ProfileAccessDto | null> {
    const response = await fetch(`${this.baseUrl}/profile/${aliasId}`, {
      headers: this.getAuthHeaders(),
      cache: 'no-store',
    });
    if (response.status === 404) {
      return null;
    }
    if (response.status === 304) {
      const retry = await fetch(`${this.baseUrl}/profile/${aliasId}?_=${Date.now()}`, {
        headers: this.getAuthHeaders(),
        cache: 'no-store',
      });
      if (retry.status === 404) {
        return null;
      }
      if (!retry.ok) {
        throw new Error('Failed to fetch profile');
      }
      const retryData = await retry.json();
      return {
        profile: retryData.profile ?? retryData,
        canEdit: retryData.canEdit ?? false,
        isTeamMemberProfile: retryData.isTeamMemberProfile ?? false,
        isPremiumAliasProfile: retryData.isPremiumAliasProfile ?? false,
      };
    }
    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }
    const data = await response.json();
    return {
      profile: data.profile ?? data,
      canEdit: data.canEdit ?? false,
      isTeamMemberProfile: data.isTeamMemberProfile ?? false,
      isPremiumAliasProfile: data.isPremiumAliasProfile ?? false,
    };
  }

  async updateProfile(aliasId: string, request: UpdateProfileRequestDto): Promise<ProfileDto> {
    const response = await fetch(`${this.baseUrl}/profile/${aliasId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      let message = 'Failed to update profile';
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }
    return response.json();
  }

  async searchProfiles(interest: string): Promise<ProfileDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/search?interest=${encodeURIComponent(interest)}`);
    if (!response.ok) {
      throw new Error('Failed to search profiles');
    }
    return response.json();
  }

  // Social Links Management
  async getSocialLinks(profileId: string): Promise<SocialLinkDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks`);
    if (!response.ok) {
      throw new Error('Failed to fetch social links');
    }
    return response.json();
  }

  async addSocialLink(profileId: string, link: CreateSocialLinkDto): Promise<SocialLinkDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(link)
    });
    if (!response.ok) {
      throw new Error('Failed to add social link');
    }
    return response.json();
  }

  async updateSocialLink(profileId: string, linkId: string, link: CreateSocialLinkDto): Promise<SocialLinkDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks/${linkId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(link)
    });
    if (!response.ok) {
      throw new Error('Failed to update social link');
    }
    return response.json();
  }

  async deleteSocialLink(profileId: string, linkId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks/${linkId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete social link');
    }
  }

  // Interests Management
  async getInterests(profileId: string): Promise<InterestDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests`);
    if (!response.ok) {
      throw new Error('Failed to fetch interests');
    }
    return response.json();
  }

  async addInterest(profileId: string, interest: CreateInterestDto): Promise<InterestDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(interest),
    });
    if (!response.ok) {
      let message = 'Failed to add interest';
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
      } catch {
        /* non-JSON body */
      }
      throw new Error(message);
    }
    return response.json();
  }

  async deleteInterest(profileId: string, interestId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests/${interestId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete interest');
    }
  }
}

export default new SocialService();

